import {Injectable} from '@angular/core';
import {BaseRiskTabService} from '../../base/service/base-risk-tab.service';
import {BaseDynamicControlModel} from '../../../../shared/dynamic-form/dynamic-form-control/base-dynamic-control.model';
import {Validators} from '@angular/forms';
import {CurrencyDynamicControlModel} from '../../../../shared/dynamic-form/dynamic-form-control/control-types/generic/currency/currency-dynamic-control.model';
import {RadioButtonYesNoDynamicControlModel} from '../../../../shared/dynamic-form/dynamic-form-control/control-types/generic/radiobutton-yesno/radiobutton-yesno-dynamic-control.model';
import {AutocompleteDynamicControlModel} from '../../../../shared/dynamic-form/dynamic-form-control/control-types/generic/autocomplete/autocomplete-dynamic-control.model';
import {AddressDynamicControlModel} from '../../../../shared/dynamic-form/dynamic-form-control/control-types/generic/address/address-dynamic-control.model';
import {LookupFilteredDropDownModel} from '../../../../shared/lookup/lookup.filtered.drop.down.model';
import {BehaviorSubject, Subject} from 'rxjs';
import {LookupModel} from '../../../../shared/lookup/lookup.model';
import {LoaderService} from '../../../../shared/screen-loader/loader.service';
import {ContentsService} from './contents.service';
import {AddressTypeModel} from '../../../../shared/msti-agm/model/address-type.model';
import {AppMangerService} from '../../../../mstiappmanager/service/app_manager.service';

@Injectable()
export class ContentsDetailsService extends BaseRiskTabService {

    private areaTypes: LookupFilteredDropDownModel;
    private neighbourhoodSetups: LookupFilteredDropDownModel;
    private propertyOccupancies: LookupFilteredDropDownModel;
    private propertyOccupantTypes: LookupFilteredDropDownModel;

    private hideAddressSelection: BehaviorSubject<boolean>;
    private hideAddressControl: BehaviorSubject<boolean>;
    private availableAddresses: Subject<LookupModel[]>;

    public parentCardId: string;

    constructor(
        private loaderService: LoaderService, private contentsService: ContentsService,
        private appManagerService: AppMangerService
    ) {
        super();
        this.areaTypes = new LookupFilteredDropDownModel([]);
        this.neighbourhoodSetups = new LookupFilteredDropDownModel([]);
        this.propertyOccupancies = new LookupFilteredDropDownModel([]);
        this.propertyOccupantTypes = new LookupFilteredDropDownModel([]);
        this.hideAddressControl = new BehaviorSubject<boolean>(true);
        this.hideAddressSelection = new BehaviorSubject<boolean>(true);
        this.availableAddresses = new Subject<LookupModel[]>();
    }

    init(): void {
        this.loaderService.show();
        this.contentsService.getAreaTypes().then(data => {
            this.areaTypes.setValues(data);
        }).then(() => this.loaderService.hide());

        this.loaderService.show();
        this.contentsService.getNeighbourSetups().then(data => {
            this.neighbourhoodSetups.setValues(data);
        }).then(() => this.loaderService.hide());

        this.loaderService.show();
        this.contentsService.getOccupancies().then(data => {
            this.propertyOccupancies.setValues(data);
        }).then(() => this.loaderService.hide());

        this.loaderService.show();
        this.contentsService.getOccupantTypes().then(data => {
            this.propertyOccupantTypes.setValues(data);
        }).then(() => this.loaderService.hide());
    }

    public getStoredAddresses(): AddressTypeModel[] {
        return this.contentsService.getAddresses();
    }

    public displayAddressSelectControl(displayAddressSelectControl: boolean) {
        this.hideAddressSelection.next(!displayAddressSelectControl);

        if (displayAddressSelectControl === true) {
            this.availableAddresses.next(this.getAvailableAddresses().filteredValues);
        }
    }

    private getAvailableAddresses() {
        return this.contentsService.getAddressLookups();
    }

    public displayAddressControl(displayAddressControl: boolean) {
        this.hideAddressControl.next(!displayAddressControl);
    }

    public clearAddressForCard() {
        this.contentsService.removeAddress(this.parentCardId);
    }

    public setUniqueAddressForCard(address: AddressTypeModel) {
        this.contentsService.addAddress(this.parentCardId, address);
    }

    public getClientPhysicalAddress(): AddressTypeModel {
        return this.contentsService.getClientHomeAddressDetails().toAddressTypeModel();
    }

    getDynamicFormControls(): BaseDynamicControlModel<any>[] {
        // Get the min max values
        const sectionValidation = this.appManagerService.getValidation('3002');
        let minVal = 7500;
        if (sectionValidation) {
            minVal = sectionValidation.field.minimumValue;
        }
        const dynamicFormControls: BaseDynamicControlModel<any>[] = [
            new CurrencyDynamicControlModel(
                {
                    key: 'contentsAmount',
                    label: 'For what amount would your client like to insure their home content?',
                    hint: '',
                    value: 0.00,
                    validation: [Validators.required, Validators.min(minVal)],
                    order: 1
                }
            ),
            new RadioButtonYesNoDynamicControlModel(
                {
                    key: 'contentsAddressSameAsPhysical',
                    label: 'Are your client\'s home content at their home address?',
                    value: '',
                    validation: [Validators.required],
                    order: 2
                }
            ),
            new AutocompleteDynamicControlModel(
                {
                    key: 'addressSelection',
                    label: 'Select your address from the list.',
                    hint: '',
                    validation: [Validators.required],
                    order: 3,
                    availableOptionsObservable: this.availableAddresses.asObservable(),
                    hidden: this.hideAddressSelection.asObservable()
                }
            ),
            new AddressDynamicControlModel(
                {
                    key: 'contentsAddress',
                    label: 'Home content risks address',
                    hint: '',
                    value: new AddressTypeModel('HOME'), // TODO - Confirm this
                    validation: [],
                    order: 4,
                    hidden: this.hideAddressControl.asObservable()
                }
            ),
            new RadioButtonYesNoDynamicControlModel(
                {
                    key: 'contentsBurglaries',
                    label: 'Were there any burglaries at this address in the past year?',
                    value: '',
                    validation: [Validators.required],
                    order: 5
                }
            ),
            new AutocompleteDynamicControlModel(
                {
                    key: 'contentsAreaType',
                    label: 'Select the area type where the property is located. ',
                    hint: '',
                    validation: [Validators.required],
                    order: 6,
                    availableOptionsObservable: new BehaviorSubject<LookupModel[]>(this.areaTypes.filteredValues).asObservable()
                }
            ),
            new AutocompleteDynamicControlModel(
                {
                    key: 'contentsNeighbourhood',
                    label: 'Select one that best describes your client\'s neighbourhood setup.',
                    hint: '',
                    validation: [Validators.required],
                    order: 7,
                    availableOptionsObservable: new BehaviorSubject<LookupModel[]>(this.neighbourhoodSetups.filteredValues).asObservable()
                }
            ),
            new AutocompleteDynamicControlModel(
                {
                    key: 'contentsPropertyOccupation',
                    label: 'When is the property occupied most?',
                    hint: '',
                    validation: [Validators.required],
                    order: 8,
                    availableOptionsObservable: new BehaviorSubject<LookupModel[]>(this.propertyOccupancies.filteredValues).asObservable()
                }
            ),
            new AutocompleteDynamicControlModel(
                {
                    key: 'contentsPropertyOccupants',
                    label: 'Who occupies the property the most?',
                    hint: '',
                    validation: [Validators.required],
                    order: 9,
                    availableOptionsObservable: new BehaviorSubject<LookupModel[]>(this.propertyOccupantTypes.filteredValues).asObservable()
                }
            )
        ];

        return dynamicFormControls.sort((a, b) => a.order - b.order);
    }

}
